import { FC } from "react";

import { Box, Button, Column, FormField, Text } from "@hightouch/htui";
import moment from "moment";

import { TimeSelect } from "src/ui/time-select";

import {
  calendarScheduleToMonthlyPickerState,
  formatMonthlyScheduleSummary,
  monthlyPickerStateToSchedule,
} from "./calendar-recurrence";
import { CalendarRecurrenceSchedule, ReportSchedule } from "./types";

interface Props {
  isDisabled: boolean;
  schedule: CalendarRecurrenceSchedule;
  setSchedule: (schedule: ReportSchedule) => void;
}

export const MonthlySchedulePicker: FC<Props> = ({
  isDisabled,
  schedule,
  setSchedule,
}) => {
  const pickerState = calendarScheduleToMonthlyPickerState(schedule);
  const summary = formatMonthlyScheduleSummary(pickerState);

  return (
    <Column gap={4}>
      <FormField label="Repeat at">
        <Column gap={2}>
          <TimeSelect
            isDisabled={isDisabled}
            timeZone="UTC"
            value={moment.utc(pickerState.time, "HH:mm").toDate()}
            onChange={(value) => {
              setSchedule(
                monthlyPickerStateToSchedule(
                  {
                    ...pickerState,
                    time: moment.utc(value).format("HH:mm"),
                  },
                  schedule,
                ),
              );
            }}
          />
          <Text color="text.tertiary" size="sm">
            Times and calendar days use UTC.
          </Text>
        </Column>
      </FormField>

      <FormField label="Repeat on">
        <Column gap={2}>
          <Box display="grid" gridTemplateColumns="repeat(7, 32px)" gap={2}>
            {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => {
              const selected = pickerState.days.includes(day);

              return (
                <Button
                  key={day}
                  aria-label={`Day ${day}`}
                  aria-pressed={selected}
                  isDisabled={isDisabled}
                  shape="circle"
                  size="sm"
                  variant={selected ? "primary" : "secondary"}
                  onClick={() => {
                    if (selected && pickerState.days.length === 1) return;

                    const days = selected
                      ? pickerState.days.filter((value) => value !== day)
                      : [...pickerState.days, day];
                    setSchedule(
                      monthlyPickerStateToSchedule(
                        { ...pickerState, days },
                        schedule,
                      ),
                    );
                  }}
                >
                  {day}
                </Button>
              );
            })}
          </Box>
          <Text color="text.tertiary" size="sm">
            In months without a selected day, it runs on the month’s last day
            instead.
          </Text>
        </Column>
      </FormField>

      {summary && <Text color="text.secondary">{summary}</Text>}
    </Column>
  );
};
